import { useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  List,
  Network,
  Map,
  FileText,
  Target,
  AlertTriangle,
  Radar,
  Database,
  Search,
  MessageSquare,
  TrendingUp,
  Users,
  Puzzle,
  ChevronDown,
  ChevronRight,
  Globe,
  PanelLeftClose,
  PanelLeftOpen,
  Scan,
  ShieldAlert,
  Monitor,
} from 'lucide-react';
import { useAuth } from '../../auth/AuthProvider';
import useStore from '../../store/useStore';
import { PATH_PERMISSION } from '../../config/permissions';

const menuSections = [
  {
    id: 1,
    title: 'General',
    items: [
      { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
      { path: '/audit-logs', icon: List, label: 'System Audit Logs' },
    ],
  },
  {
    id: 2,
    title: 'Assets & Defense',
    items: [
      { path: '/network-map', icon: Network, label: 'Network Map' },
      { path: '/scanner', icon: Scan, label: 'Scanner' },
      { path: '/assets', icon: Map, label: 'MyAssets' },
      { path: '/agents', icon: Monitor, label: 'Agent Registry' },
      { path: '/sbom', icon: FileText, label: 'SBOM Records' },
      { path: '/baseline', icon: Target, label: 'Baseline Management' },
      { path: '/drift-alerts', icon: AlertTriangle, label: 'Active Alerts' },
    ],
  },
  {
    id: 3,
    title: 'Intelligence',
    items: [
      { path: '/vulnerabilities', icon: ShieldAlert, label: 'Vulnerabilities' },
      { path: '/mycert', icon: Radar, label: 'Local Insights (MyCERT)' },
      { path: '/feed-monitoring', icon: Database, label: 'Feed Monitoring' },
      { path: '/indicator-vault', icon: Search, label: 'Indicator Vault' },
      { path: '/advisor', icon: MessageSquare, label: 'ChatOps Console' },
      { path: '/risk-assessment', icon: TrendingUp, label: 'Risk Assessment' },
    ],
  },
  {
    id: 4,
    title: 'Management',
    items: [
      { path: '/org/members', icon: Users, label: 'Members & Roles' },

      { path: '/integrations', icon: Puzzle, label: 'Integration Setup' },
    ],
  },
];

const STORAGE_KEY_PREFIX = 'sidebar_section_';

const loadCollapseState = (sectionId) => {
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}${sectionId}`);
    return stored === 'true';
  } catch {
    return false;
  }
};

const saveCollapseState = (sectionId, collapsed) => {
  try {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${sectionId}`, String(collapsed));
  } catch {
    // localStorage unavailable — no-op
  }
};

const Sidebar = () => {
  const location = useLocation();
  const { user, permissions, pageAccess } = useAuth();
  const { sidebarMinimized, setSidebarMinimized } = useStore();
  const isSuperadmin = (user?.roles || []).includes('Superadmin');
  const [collapsed, setCollapsed] = useState(() => {
    const state = {};
    menuSections.forEach((s) => {
      state[s.id] = loadCollapseState(s.id);
    });
    state['platform'] = loadCollapseState('platform');
    return state;
  });

  const toggleSection = useCallback((sectionId) => {
    setCollapsed((prev) => {
      const next = !prev[sectionId];
      saveCollapseState(sectionId, next);
      return { ...prev, [sectionId]: next };
    });
  }, []);

  return (
    <aside className={`${sidebarMinimized ? 'w-16' : 'w-60'} bg-navy-900 flex flex-col h-screen fixed left-0 top-0 z-30 transition-all duration-300 ease-in-out ${sidebarMinimized ? 'overflow-hidden' : ''}`} aria-expanded={!sidebarMinimized}>
      {/* Logo */}
      <div className="h-16 flex items-center px-4 border-b border-navy-800 shrink-0">
        <div className="w-8 h-8 bg-cyan-400 rounded-lg flex items-center justify-center shrink-0">
          <span className="text-navy-900 font-bold text-sm">S</span>
        </div>
        {!sidebarMinimized && <span className="text-white font-semibold text-lg ml-3">UM-DragonEye</span>}
        <button
          type="button"
          onClick={setSidebarMinimized}
          className="p-1.5 rounded-lg hover:bg-navy-800 text-slate-400 hover:text-white transition-colors ml-auto"
          aria-label={sidebarMinimized ? "Expand sidebar" : "Collapse sidebar"}
        >
          {sidebarMinimized ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {menuSections.map((section) => (
          <div key={section.id} className="mb-2">
            {/* Section Header */}
            {!sidebarMinimized && (
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between px-4 py-2 mt-2 text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-300 transition-colors"
              >
                <span>{section.id}. {section.title}</span>
                {collapsed[section.id] ? (
                  <ChevronRight className="w-3.5 h-3.5" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5" />
                )}
              </button>
            )}

            {/* Section Items */}
            {!collapsed[section.id] && (
              <div className="px-3 space-y-1">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  const requiredPerm = PATH_PERMISSION[item.path];
                  // Users with no org bypass sidebar page gates — ProtectedRoute renders NoOrgState for them
                  // OLD (permission-based): const hasPermission = !requiredPerm || (permissions || []).includes(requiredPerm) || !user?.tenant_id;
                  const hasPageAccess = !requiredPerm || (pageAccess || []).includes(item.path) || !user?.tenant_id;

                  return (
                    <Link
                      key={item.path}
                      to={hasPageAccess ? item.path : '#'}
                      onClick={e => { if (!hasPageAccess) e.preventDefault(); }}
                      className={`flex items-center ${sidebarMinimized ? 'justify-center px-0' : 'space-x-3 px-3'} py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive
                        ? 'bg-cyan-400 text-white'
                        : hasPageAccess
                          ? 'text-slate-400 hover:text-white hover:bg-navy-800'
                          : 'text-slate-600 opacity-40 cursor-not-allowed'
                        }`}
                    >
                      <Icon className="w-5 h-5 shrink-0" />
                      {!sidebarMinimized && <span>{item.label}</span>}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        ))}

        {/* Platform section — Superadmin only */}
        {isSuperadmin && (
          <div className="mb-2">
            {!sidebarMinimized && (
              <button
                onClick={() => toggleSection('platform')}
                className="w-full flex items-center justify-between px-4 py-2 mt-2 text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-300 transition-colors"
              >
                <span>5. Platform</span>
                {collapsed['platform'] ? (
                  <ChevronRight className="w-3.5 h-3.5" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5" />
                )}
              </button>
            )}
            {!collapsed['platform'] && (
              <div className="px-3 space-y-1">
                <Link
                  to="/platform/organizations"
                  className={`flex items-center ${sidebarMinimized ? 'justify-center px-0' : 'space-x-3 px-3'} py-2.5 rounded-lg text-sm font-medium transition-colors ${location.pathname === '/platform/organizations'
                      ? 'bg-cyan-400 text-white'
                      : 'text-slate-400 hover:text-white hover:bg-navy-800'
                    }`}
                >
                  <Globe className="w-5 h-5 shrink-0" />
                  {!sidebarMinimized && <span>Organizations</span>}
                </Link>
              </div>
            )}
          </div>
        )}
      </nav>

      {/* System Health */}
      {!sidebarMinimized && (
        <div className="p-4 border-t border-navy-800 shrink-0">
          <div className="bg-navy-800 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-slate-400 uppercase">System Health</span>
              <span className="text-xs text-success font-medium">Stable</span>
            </div>
            <div className="w-full bg-navy-900 h-1.5 rounded-full overflow-hidden">
              <div className="bg-success w-4/5 h-full rounded-full"></div>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
